import React, { memo, useLayoutEffect, useMemo, useRef, useState } from "react";
import TableSkeleton, {
  CompactSkeleton,
  FormSkeleton,
} from "./TableSkeleton";
import "./TableSkeleton.scss";

/**
 * Drop-in loading UI used across the app.
 * Renders skeleton placeholders instead of a spinner.
 * Always inline — never covers page titles / table headers.
 * Props API is unchanged so existing call sites stay intact.
 */
const LoaderComponent = memo(
  ({ className, size = 150, padding = "p-5", columns }) => {
    const baseSize =
      typeof size === "number"
        ? size
        : Number.parseFloat(String(size).replace("px", "")) || 150;

    const hostRef = useRef(null);

    const [isModal, setIsModal] = useState(false);

    const wrapperClassName = useMemo(() => {
      return (
        "d-flex justify-content-center align-items-center loader-skeleton-host" +
        (className ? " " + className : "") +
        (padding ? " " + padding : "")
      );
    }, [className, padding]);

    useLayoutEffect(() => {
      const host = hostRef.current;
      if (!host) return;
      setIsModal(!!host.closest(".modal-container"));
    }, []);

    const style = {
      width: "100%",
      minHeight: baseSize <= 50 ? undefined : 200,
    };

    let content;
    if (baseSize <= 50) {
      content = <CompactSkeleton />;
    } else if (isModal || baseSize < 100) {
      content = <FormSkeleton rows={baseSize >= 80 ? 4 : 3} />;
    } else {
      const rows = baseSize >= 140 ? 8 : baseSize >= 100 ? 6 : 4;
      content = (
        <div style={{ width: "100%" }}>
          <TableSkeleton
            rows={rows}
            columns={columns || 12}
            minHeight={baseSize}
            showHeader={false}
          />
        </div>
      );
    }

  return (
    <div ref={hostRef} className={wrapperClassName} style={style}>
      {content}
    </div>
  );
});

LoaderComponent.displayName = "LoaderComponent";

export default LoaderComponent;
